import React from 'react';
import './tome-tokens.css';
import './tome-shape.css';
import { type TomeCorners, type TomeEdge, type TomeShapeSpec, tomeOutline, tomeRule, tomeStains } from './tomeShape';
import { ensureTomeTextures } from './tomeTexture';
import type { TomeTexture } from './tomeVibes';

export type TomeSkinLayer = 'cover' | 'sheet' | 'leaf' | 'entry';

/** The shape props every shaped tome piece takes. */
export interface TomeShapeProps {
  /** `deckled`: fine hand-made paper edge. `torn`: ragged bites. `clean`: straight. */
  edge?: TomeEdge;
  /** `notched` scoops, `chamfer` cuts, `round` curves, `square` leaves the corner. */
  corners?: TomeCorners;
  /** Multiplier on edge depth (0 = clean, 1 = the layer's token depth). */
  roughness?: number;
  /** Seeds the irregularity, so a given window always keeps the same silhouette. */
  shapeSeed?: string | number;
}

export interface TomeSkinProps extends TomeShapeProps {
  layer: TomeSkinLayer;
  /** `vellum`: grain and fibres. `aged`: also tide stains and a heavier edge burn. `plain`: flat. */
  texture?: TomeTexture;
  /** Draw the ruled border inside the edge: `double` (a page's gilt rule) or `single`. */
  rule?: 'none' | 'single' | 'double';
  /** Tide stains on the page; defaults to 3 for `aged` paper, else none. */
  stains?: number;
  className?: string;
}

/*
 * One ResizeObserver for every skin. A skin redraws only when its box changes
 * size (open, a page that grows, a window resize), never per frame.
 */
type Redraw = (width: number, height: number) => void;
const redraws = new WeakMap<Element, Redraw>();
let observer: ResizeObserver | null = null;
function observe(element: Element, redraw: Redraw): () => void {
  redraws.set(element, redraw);
  if (typeof ResizeObserver === 'undefined') return () => { redraws.delete(element); };
  observer ??= new ResizeObserver(entries => {
    for (const entry of entries) {
      const box = entry.borderBoxSize?.[0];
      redraws.get(entry.target)?.(box ? box.inlineSize : entry.contentRect.width, box ? box.blockSize : entry.contentRect.height);
    }
  });
  observer.observe(element);
  return () => { observer?.unobserve(element); redraws.delete(element); };
}

const LAYER_DEFAULTS: Record<TomeSkinLayer, { corner: number; deckle: number; warp: number }> = {
  cover: { corner: 26, deckle: 1.6, warp: 4.5 },
  sheet: { corner: 19, deckle: 2.2, warp: 2.5 },
  leaf: { corner: 15, deckle: 2, warp: 1.5 },
  entry: { corner: 8, deckle: 1, warp: 0.6 },
};

/** Shape tokens are CSS custom properties (`--tome-shape-corner`, `-deckle`, `-warp`) so themes and breakpoints can tune them. */
function readToken(style: CSSStyleDeclaration, name: string, fallback: number): number {
  const value = parseFloat(style.getPropertyValue(name));
  return Number.isFinite(value) ? value : fallback;
}

/**
 * The shaped, textured ground of a tome piece: an absolutely placed layer
 * behind the piece's content (the host needs `position` and `isolation`).
 * Only this layer is clipped, so content, focus rings, tooltips and ornaments
 * that overhang the edge are never cut. Shadows are filter-free, because the
 * page re-rasterises whenever something on it repaints and a blur filter would
 * re-run each time: a contact shadow (the same outline, offset and darkened)
 * follows the shape where it meets the surface, and the soft far shadow is an
 * analytic box-shadow whose few px of shape difference vanish in its blur.
 * Decorative: aria-hidden, no pointer events.
 */
export const TomeSkin: React.FC<TomeSkinProps> = ({
  layer, edge = 'deckled', corners = 'round', roughness = 1, shapeSeed = layer,
  texture = 'vellum', rule = 'none', stains, className = '',
}) => {
  const stainCount = stains ?? (texture === 'aged' ? 3 : 0);
  const rootRef = React.useRef<HTMLSpanElement>(null);
  const contactRef = React.useRef<HTMLSpanElement>(null);
  const fillRef = React.useRef<HTMLSpanElement>(null);
  const wearRef = React.useRef<SVGSVGElement>(null);

  React.useLayoutEffect(() => {
    ensureTomeTextures();
    const root = rootRef.current; const fill = fillRef.current; const wear = wearRef.current; const contact = contactRef.current;
    if (!root || !fill || !wear) return undefined;
    let last = '';
    const redraw: Redraw = (width, height) => {
      const w = Math.round(width); const h = Math.round(height);
      const key = `${w}x${h}`;
      if (key === last || w <= 0 || h <= 0) return;
      last = key;
      const style = getComputedStyle(root);
      const defaults = LAYER_DEFAULTS[layer];
      const spec: TomeShapeSpec = {
        edge, corners, roughness, seed: `${layer}:${shapeSeed}`, cornerSeed: shapeSeed,
        corner: readToken(style, '--tome-shape-corner', defaults.corner),
        deckle: readToken(style, '--tome-shape-deckle', defaults.deckle),
        // A vibe scales every layer's bow at once (--tome-bow on the panel).
        warp: readToken(style, '--tome-shape-warp', defaults.warp) * readToken(style, '--tome-bow', 1),
      };
      const outline = tomeOutline(w, h, spec);
      fill.style.clipPath = `path('${outline}')`;
      if (contact) contact.style.clipPath = fill.style.clipPath;
      wear.setAttribute('viewBox', `0 0 ${w} ${h}`);
      wear.querySelectorAll('[data-part="edge"]').forEach(path => path.setAttribute('d', outline));
      wear.querySelector('[data-part="rule"]')?.setAttribute('d', tomeRule(w, h, spec, 5));
      wear.querySelector('[data-part="rule-inner"]')?.setAttribute('d', tomeRule(w, h, spec, 8));
      const stains = wear.querySelector('[data-part="stains"]');
      if (stains) {
        stains.replaceChildren(...tomeStains(w, h, spec.seed, stainCount).map(stain => {
          const ellipse = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
          for (const [key, value] of Object.entries(stain)) ellipse.setAttribute(key, String(value));
          return ellipse;
        }));
      }
    };
    const rect = root.getBoundingClientRect();
    redraw(rect.width, rect.height);
    return observe(root, redraw);
  }, [layer, edge, corners, roughness, shapeSeed, texture, rule, stainCount]);

  // Draw only the parts this layer shows: every element here is paint work for the whole page.
  const bands = layer === 'entry' ? ['line'] : texture === 'plain' ? ['mid', 'line'] : ['wide', 'mid', 'line'];
  return <span ref={rootRef} className={`tome-skin ${className}`} data-layer={layer} data-edge={edge}
    data-corners={corners} data-texture={texture} aria-hidden="true">
    {layer !== 'entry' && <span ref={contactRef} className="tome-skin-contact" />}
    <span ref={fillRef} className="tome-skin-fill">
      {texture !== 'plain' && <span className="tome-skin-paper" />}
      <svg ref={wearRef} className="tome-skin-wear" preserveAspectRatio="none" focusable="false">
        {stainCount > 0 && <g data-part="stains" className="tome-skin-stains" />}
        {/* Edge wear: strokes on the outline, clipped to their inner half, darken toward the edge. */}
        {bands.map(band => <path key={band} data-part="edge" className="tome-skin-edge" data-band={band} />)}
        {rule !== 'none' && <path data-part="rule" className="tome-skin-rule" />}
        {rule === 'double' && <path data-part="rule-inner" className="tome-skin-rule" data-inner="true" />}
      </svg>
    </span>
  </span>;
};
